import BotPreviewCard from "features/bots/MyBots/BotPreviewCard";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import React, { useContext, useMemo } from "react";
import { useTheme } from "styled-components";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import styled from "styled-components";
import { BasicBotData, BotType } from "@shared/types";
import { CardsSelectorContext } from "@shared/common/CardsList/CardsSelector.context";
import useCardsListSelector from "@shared/common/CardsList/useCardsListSelector";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import { openMessage } from "utils/misc";
import { CardsLayoutContext } from "features/bots/BotForm/common/CardsLayoutProvider";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { useBottomNavigator } from "utils/hooks/misc";
import MobileBotsCards from "features/bots/MyBots/common/MobileBotsCards";
import { useCanManageBotType } from "@shared/features/bots/hooks";
import NewLookBotCard from "features/bots/NewLook/NewLookBotCard";
import { newLookCardSurface } from "features/bots/NewLook/newLookStyles";
import NewLookBotCardSkeleton from "features/bots/NewLook/NewLookBotCardSkeleton";
import useNewLookBotSparklines, {
  getNewLookSparkline,
} from "features/bots/NewLook/useNewLookBotSparklines";
import { NEW_LOOK_CARDS_VIEW_MAX_WIDTH } from "common/NewLook/layout.ts";

interface BotsCardsProps {
  emptyState?: React.JSX.Element;
}

const BotsCards = ({ emptyState }: BotsCardsProps) => {
  const appTheme = useTheme();
  const { botType, bots, isFetching, isChannelConfiguration } =
    useContext(MyBotsPageContext)!;
  const { pageWidth, numRowItems } = useContext(CardsLayoutContext)!;
  const { isBulkMode, selectedBotsIDs, setSelectedBotsIDs } = useContext(
    BotsBulkActionsContext
  )!;
  const { getCheckboxProps, onSelectRow } = useCardsListSelector<
    BasicBotData,
    number
  >({
    selectedRows: selectedBotsIDs,
    setSelectedRows: setSelectedBotsIDs,
    getKey: (botData) => botData.id,
    openMessage,
  });
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const canManageBotType = useCanManageBotType(botType);
  // New look: each card's sparkline, from the bots graphs the performance
  // panel above already loads (none where that panel isn't shown).
  const hasSparklines =
    !!appTheme.isNewLook &&
    !isChannelConfiguration &&
    botType !== BotType.TRADING_VIEW_SIGNALS;
  const sparklines = useNewLookBotSparklines(botType, hasSparklines);

  const onBulkUpdatePress = (bot: BasicBotData) => {
    if (!canManageBotType) return;
    onSelectRow(bot, true);
  };

  const content = useMemo(() => {
    if (isBottomNavigator) {
      return (
        <MobileBotsCards emptyState={emptyState} sparklines={sparklines} />
      );
    }
    if (appTheme.isNewLook) {
      // First load: card-shaped placeholders instead of a spinner.
      if (isFetching && !bots?.length) {
        return (
          <Wrapper id="my-bots">
            <NewLookGrid>
              {[0, 1, 2].map((key) => (
                <NewLookBotCardSkeleton key={key} />
              ))}
            </NewLookGrid>
          </Wrapper>
        );
      }
      return (
        <Wrapper id="my-bots">
          <LoadingSpinnerOverlay
            isLoading={isFetching}
            spinnerMarginTop={50}
            spinnerSize="large"
            backgroundColor={appTheme.colors.layoutBgColor}
          >
            {bots && bots.length > 0 && (
              <NewLookGrid>
                {bots.map((bot, i) => (
                  <NewLookBotCard
                    key={bot.id}
                    botData={bot}
                    id={`my-bot-${i + 1}`}
                    sparkline={getNewLookSparkline(sparklines, bot.id)}
                  />
                ))}
              </NewLookGrid>
            )}
            {bots?.length === 0 && !isFetching && (
              <NewLookEmpty>{emptyState}</NewLookEmpty>
            )}
          </LoadingSpinnerOverlay>
        </Wrapper>
      );
    }
    return (
      <Wrapper id="my-bots">
        <LoadingSpinnerOverlay
          isLoading={isFetching || !pageWidth}
          spinnerMarginTop={50}
          spinnerSize="large"
          backgroundColor={appTheme.colors.layoutBgColor}
        >
          {bots && bots.length > 0 && !!pageWidth && (
            <EqualColumnsGrid
              numColumns={numRowItems}
              style={{ width: "100%" }}
            >
              {bots?.map((bot, i) => (
                <BotPreviewCard
                  key={bot.id}
                  botData={bot}
                  id={`my-bot-${i + 1}`}
                />
              ))}
            </EqualColumnsGrid>
          )}
          {bots?.length === 0 && !isFetching && emptyState}
        </LoadingSpinnerOverlay>
      </Wrapper>
    );
  }, [
    isFetching,
    bots,
    numRowItems,
    botType,
    pageWidth,
    isBottomNavigator,
    appTheme.isNewLook,
    sparklines,
  ]);

  const contextValue = useMemo(() => {
    return {
      isEditMode: isBulkMode,
      setIsEditMode: onBulkUpdatePress,
      getCheckboxProps,
    };
  }, [isBulkMode, getCheckboxProps]);

  return (
    <CardsSelectorContext.Provider value={contextValue}>
      {content}
    </CardsSelectorContext.Provider>
  );
};

export default BotsCards;

export const MIN_CARD_WIDTH = 445;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const NewLookGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr));
  @media (max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH}) {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 330px), 1fr));
  }
  gap: 16px;
`;
// New look: a filtered-out list says so inside a panel, not on the canvas.
const NewLookEmpty = styled.div`
  flex: 1;
  min-width: 0;
  padding: 32px 20px;
  ${newLookCardSurface};
`;
